import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { GET_TINYCMS_SITE } from '../../graphql/queries';
import { CmsSite } from './CmsSite';
import { InkSection } from './shared';
import { AnalyticsSettings } from '../settings/Analytics';
import { RootFilesSettings } from '../settings/RootFiles';
import { ManageSiteHealth } from './SiteHealth';
import { ManageArea, MANAGE_AREAS, manageUrl } from './manageAreas';

/**
 * Content → Manage (TinyCMS UX optimization review, 2026-10-24): every
 * setting for the site and its content in one place, under the Content
 * header, for what belongs to the whole site (backlog 46b884ba). Settings for one
 * kind of content are a Settings button on its own tab.
 *
 *   ?view=manage&area=site|design|code|health
 *
 * Distribution (licensed feeds and their keys) moved to Account → Connections
 * on 2026-10-24; ?area=dist goes there.
 *
 * The old addresses (/site/settings, ?view=design) redirect here (manageRedirects.tsx).
 */

export * from './manageAreas';

function ManageHead({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mg-head">
      <h2 className="mg-title">{title}</h2>
      <p className="mg-sub">{children}</p>
    </div>
  );
}

export function ManageView({ area }: { area: ManageArea }) {
  const navigate = useNavigate();
  const { hash } = useLocation();
  // A link to one section (#analytics, #fields): there once the area has drawn it.
  useEffect(() => {
    const id = hash.replace(/^#/, '');
    if (!id) return undefined;
    let tries = 0;
    const t = window.setInterval(() => {
      const el = document.getElementById(id);
      if (el || ++tries > 20) {
        window.clearInterval(t);
        el?.scrollIntoView({ block: 'start' });
      }
    }, 150);
    return () => window.clearInterval(t);
  }, [area, hash]);
  return (
    <div className="mg">
      <nav className="mg-rail" aria-label="Manage">
        {MANAGE_AREAS.map(([key, label]) => (
          <button
            type="button"
            key={key}
            className="mg-rail-item"
            data-active={key === area ? 'true' : undefined}
            aria-current={key === area ? 'page' : undefined}
            onClick={() => navigate(manageUrl(key))}
          >
            {label}
          </button>
        ))}
      </nav>
      <div className="mg-body">
        {area === 'site' ? <ManageSite /> : null}
        {area === 'health' ? <ManageSiteHealth /> : null}
        {area === 'design' ? (
          <>
            <ManageHead title="Design">
              The template, colours and fonts of the public site, what agents may change, and every
              published theme.
            </ManageHead>
            <CmsSite mode="design" />
          </>
        ) : null}
        {area === 'code' ? <ManageCode /> : null}
      </div>
    </div>
  );
}

function useSiteName() {
  const { accountId, selectedAccount } = useAccount();
  const { data } = useQuery(GET_TINYCMS_SITE, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-first',
  });
  const site = data?.tinycmsSite;
  return {
    site,
    name: site?.working?.identity?.title || selectedAccount?.name || 'your site',
  };
}

function ManageSite() {
  const { name } = useSiteName();
  return (
    <>
      <ManageHead title="Site">
        Who can see {name}, its address and what runs on it. How it looks lives in{' '}
        <Link to={manageUrl('design')}>Design</Link>.
      </ManageHead>
      <CmsSite mode="settings" />
    </>
  );
}

function ManageCode() {
  const { name } = useSiteName();
  return (
    <>
      <ManageHead title="Code & tracking">
        Measurement, consent and custom code on the public site, plus how search engines and ad
        networks verify {name}.
      </ManageHead>
      <InkSection
        id="analytics"
        title="Analytics and code"
        description="Runs on the public site only. Previews never count as visits."
      >
        <AnalyticsSettings embedded />
      </InkSection>
      <InkSection
        id="verification"
        title="Verification & ads.txt"
        description="Show Google, Bing and others that the site is yours, and list who may sell ads on it. Live right away — no publishing needed."
      >
        <RootFilesSettings />
      </InkSection>
    </>
  );
}
